import React, { useEffect, useMemo, useState } from 'react';
import {
  CalendarDays, ChevronDown, ChevronRight, ClipboardList, Loader2, Search, Star,
  Ticket, Trophy,
} from 'lucide-react';
import TeamBadge from '../../components/TeamBadge';
import EventRow, { OddButton } from './EventRow';
import BetSlip from './BetSlip';
import useSportsbook from './useSportsbook';
import SportsUnavailable from './SportsUnavailable';
import { bookCompetitionId } from '../../lib/sportsbook';
import './sports.css';

// Spor sayfası. Düzen referans sportsbook arayüzünden alındı: solda gri ray
// (arama → hızlı linkler → öne çıkan ligler → zaman filtresi → tüm sporlar),
// ortada kırmızı bölüm bandıyla açılan liste, sağda kupon.
//
// Bileşenler yalnızca çizer; veri ve kupon mantığı useSportsbook.js'tedir.

// Spor dalı ikonları referansta renkli toplardır; emoji aynı etkiyi tek
// karakterle verir ve ikon paketine bağımlılık getirmez.
const SPORT_ICONS = {
  SOCCER: '⚽',
  BASKETBALL: '🏀',
  TENNIS: '🎾',
  VOLLEYBALL: '🏐',
  BASEBALL: '⚾',
  ICE_HOCKEY: '🏒',
  TABLE_TENNIS: '🏓',
  HANDBALL: '🤾',
  AMERICAN_FOOTBALL: '🏈',
  BOXING: '🥊',
  MMA: '🥊',
  CRICKET: '🏏',
  GOLF: '⛳',
  DARTS: '🎯',
  RUGBY_LEAGUE: '🏉',
  RUGBY_UNION: '🏉',
  AUSTRALIAN_RULES_FOOTBALL: '🏉',
  LACROSSE: '🥍',
  SNOOKER: '🎱',
};

const sportIcon = (sport) => SPORT_ICONS[sport] || '🏆';

// Referanstaki "Öne Çıkan Ligler" kısayolu. Katalogda karşılığı olmayan lig
// listeye hiç girmez — ölü bağlantı göstermek yerine kısayol kısalır.
const FEATURED_LEAGUES = [
  { label: 'Süper Lig', match: /s(ü|u)per\s*lig/i },
  { label: 'Premier Lig', match: /premier\s*league/i },
  { label: 'Lig 1', match: /ligue\s*1/i },
  { label: 'Bundesliga', match: /^bundesliga/i },
  { label: 'Seri A', match: /serie\s*a/i },
  { label: 'LaLiga', match: /la\s*liga|laliga/i },
  { label: 'MLS', match: /\bmls\b|major\s*league\s*soccer/i },
];

// Zaman süzgeci referansta dört düğmedir. Sunucu gün bazlı çalıştığı için
// pencere `days` ile daraltılır; "2 saat" gün cinsinden istenemediğinden
// listeye ayrıca istemcide uygulanır (`cutoffHours`).
const RANGES = [
  { key: 'all', label: 'Tümü', days: 7 },
  { key: 'today', label: 'Bugün', days: 1 },
  { key: '2h', label: '2 Saat', days: 1, cutoffHours: 2 },
  { key: '3d', label: '3 Gün', days: 3 },
];

const formatTime = (iso) => {
  try {
    return new Date(iso).toLocaleString('tr-TR', {
      weekday: 'short', day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
};

const SportsPage = () => {
  const sb = useSportsbook();
  const {
    tab, setTab, bookMode, q, setQ, list, loading, serviceMessage,
    bookCompetitions, bookSports, bookList, bookEventCounts, bookLoading,
    bookSport, bookCompetition, bookFeed, setBookDays,
    selectBookSport, selectBookCompetition, clearBookFilters,
    coupon, addPick, addBookPick, loadBookEventMarkets, totalOdd,
  } = sb;

  const [range, setRange] = useState('all');
  // Kupon paneli varsayılan kapalı; ilk seçimde açılır.
  const [slipOpen, setSlipOpen] = useState(false);
  // Referansta liste açılışta kısadır ve "Tümünü Göster" ile uzar; yüzlerce
  // satırı birden çizmek sayfayı ağırlaştırıyordu.
  const [expandedAll, setExpandedAll] = useState(false);

  useEffect(() => {
    document.body.classList.add('sb-page');
    return () => document.body.classList.remove('sb-page');
  }, []);

  // İlk seçim kuponu açar, son seçim silinince kapanır. Aradaki durumu
  // oyuncu kendisi yönetir — her eklemede yeniden açmak, elle kapattığı
  // paneli inatla geri getirirdi.
  useEffect(() => {
    setSlipOpen((open) => (coupon.length > 0 ? (open || coupon.length === 1) : false));
  }, [coupon.length]);

  // Zaman düğmesi sunucu penceresini de ayarlar.
  useEffect(() => {
    const option = RANGES.find((item) => item.key === range) || RANGES[0];
    setBookDays(option.days);
  }, [range, setBookDays]);

  const selectedCompetitionId = bookCompetition
    ? `${bookFeed || 'sportsbook'}:${bookCompetition}`
    : '';
  const mobileCompetitions = bookSport
    ? (bookSports[bookSport] || [])
    : bookCompetitions;

  const selectMobileCompetition = (event) => {
    const competitionId = event.target.value;
    if (!competitionId) {
      if (bookSport) selectBookSport(bookSport);
      else clearBookFilters();
      return;
    }
    const competition = bookCompetitions.find(
      (item) => bookCompetitionId(item) === competitionId);
    if (competition) selectBookCompetition(competition);
  };

  // "2 Saat" penceresi yalnızca istemcide daraltılabilir (sunucu gün alır).
  const rows = useMemo(() => {
    const source = bookMode ? bookList : list;
    const option = RANGES.find((item) => item.key === range);
    if (!option?.cutoffHours) return source;
    const limit = Date.now() + option.cutoffHours * 3600 * 1000;
    return source.filter((row) => {
      const start = new Date(row.start_time || row.date).getTime();
      return !Number.isFinite(start) || row.live || start <= limit;
    });
  }, [bookMode, bookList, list, range]);

  const featured = useMemo(() => FEATURED_LEAGUES
    .map((item) => ({
      ...item,
      competition: bookCompetitions.find((comp) => item.match.test(comp.name || '')),
    }))
    .filter((item) => item.competition), [bookCompetitions]);

  const groups = useMemo(() => {
    const grouped = new Map();
    rows.forEach((row) => {
      const name = bookMode
        ? (row.competition_name || row.sport_label || 'Diğer')
        : (row.league || 'Diğer');
      if (!grouped.has(name)) {
        grouped.set(name, {
          name,
          sport: bookMode ? row.sport : 'SOCCER',
          sportLabel: bookMode ? row.sport_label : '',
          events: [],
        });
      }
      grouped.get(name).events.push(row);
    });
    return [...grouped.values()];
  }, [bookMode, rows]);

  const busy = bookMode ? bookLoading : loading;
  const total = rows.length;
  const visibleGroups = expandedAll ? groups : groups.slice(0, 3);
  const hiddenCount = total - visibleGroups.reduce(
    (sum, group) => sum + group.events.length, 0);

  const sportEntries = Object.entries(bookSports);

  if (sb.unavailable) return <SportsUnavailable />;

  return (
    <div className={`sb-shell ${slipOpen ? '' : 'slip-closed'}`}>
      {/* ---- Üst sekmeler ---- */}
      <div className="sb-tabbar">
        <button
          type="button"
          className={`sb-tabbar-item ${tab !== 'live' ? 'active' : ''}`}
          onClick={() => setTab(bookCompetitions.length ? 'book' : 'all')}
        >
          Spor Bahisleri
        </button>
        <button
          type="button"
          className={`sb-tabbar-item ${tab === 'live' ? 'active' : ''}`}
          onClick={() => setTab('live')}
        >
          Canlı Bahis
        </button>
      </div>

      <div className="sb-body">
        {/* ---- Sol ray ---- */}
        <aside className="sb-rail">
          <div className="sb-rail-search">
            <Search size={15} />
            <input
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder="Takım veya müsabaka ara"
            />
          </div>

          <div className="sb-rail-title">Hızlı Linkler</div>
          <button
            type="button"
            className="sb-quick"
            onClick={() => { setTab('live'); clearBookFilters(); }}
          >
            <Star size={16} className="sb-quick-icon" /> Canlı Maçlar
          </button>
          <button
            type="button"
            className="sb-quick"
            onClick={() => { setRange('today'); setTab('book'); }}
          >
            <CalendarDays size={16} className="sb-quick-icon" /> Bugünün Maçları
          </button>
          <button type="button" className="sb-quick" onClick={clearBookFilters}>
            <ClipboardList size={16} className="sb-quick-icon" /> Tüm Müsabakalar
          </button>

          {featured.length > 0 && (
            <>
              <div className="sb-rail-title">Öne Çıkan Ligler</div>
              {featured.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  className={`sb-quick ${selectedCompetitionId === bookCompetitionId(item.competition) ? 'active' : ''}`}
                  onClick={() => selectBookCompetition(item.competition)}
                >
                  <span className="sb-quick-emoji">⚽</span> {item.label}
                </button>
              ))}
            </>
          )}

          <div className="sb-range">
            {RANGES.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`sb-range-btn ${range === item.key ? 'active' : ''}`}
                onClick={() => setRange(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="sb-rail-title">Tüm Sporlar</div>
          <button
            type="button"
            className={`sb-sport-row ${!bookSport && !bookCompetition ? 'active' : ''}`}
            onClick={clearBookFilters}
          >
            <span className="sb-quick-emoji">🏆</span>
            <span className="sb-sport-name">Tümü</span>
            <span className="sb-sport-count">{bookEventCounts.total}</span>
            <ChevronRight size={14} />
          </button>
          {sportEntries.map(([sport, comps]) => (
            <button
              key={sport}
              type="button"
              className={`sb-sport-row ${bookSport === sport ? 'active' : ''}`}
              onClick={() => selectBookSport(sport)}
            >
              <span className="sb-quick-emoji">{sportIcon(sport)}</span>
              <span className="sb-sport-name">{comps[0]?.sport_label || sport}</span>
              <span className="sb-sport-count">{bookEventCounts.sports[sport] || 0}</span>
              <ChevronRight size={14} />
            </button>
          ))}
          {bookCompetitions.length === 0 && (
            <div className="sb-rail-note">
              Lig listesi yalnızca konsensüs oran kaynağı açıkken görünür.
            </div>
          )}
        </aside>

        {/* ---- Orta: liste ---- */}
        <main className="sb-main">
          <label className="sb-mobile-league">
            <Trophy size={14} />
            <span className="sr-only">Lig seç</span>
            <select
              value={selectedCompetitionId}
              onChange={selectMobileCompetition}
              disabled={!bookMode || bookCompetitions.length === 0}
            >
              <option value="">
                {bookSport
                  ? `${bookSports[bookSport]?.[0]?.sport_label || bookSport}: Tüm ligler`
                  : 'Tüm ligler'}
              </option>
              {mobileCompetitions.map((competition) => (
                <option
                  key={bookCompetitionId(competition)}
                  value={bookCompetitionId(competition)}
                >
                  {!bookSport && competition.sport_label
                    ? `${competition.sport_label} • `
                    : ''}
                  {competition.name}
                </option>
              ))}
            </select>
          </label>

          <div className="sb-panel">
            {/* Bölüm bandı: solda listenin adı, sağda spor sekmeleri. */}
            <div className="sb-section-head">
              <span className="sb-section-title">
                {tab === 'live' ? 'Popüler Canlı Maçlar' : 'Yaklaşan Maçlar'}
              </span>
              <div className="sb-section-tabs">
                {sportEntries.map(([sport, comps]) => (
                  <button
                    key={sport}
                    type="button"
                    className={`sb-section-tab ${bookSport === sport ? 'active' : ''}`}
                    onClick={() => selectBookSport(sport)}
                  >
                    <span className="sb-section-tab-icon">{sportIcon(sport)}</span>
                    {comps[0]?.sport_label || sport}
                  </button>
                ))}
              </div>
            </div>

            {busy && (
              <div className="sb-state">
                <Loader2 size={16} className="animate-spin" />
                Maçlar yükleniyor...
              </div>
            )}

            {!busy && total === 0 && (
              <div className="sb-state">
                {bookMode
                  ? (tab === 'live'
                    ? 'Şu anda oynanan canlı maç yok'
                    : 'Seçili pencerede oranlı maç bulunamadı')
                  : (serviceMessage || 'Maç bulunamadı')}
              </div>
            )}

            {!busy && visibleGroups.map((group) => (
              <section key={group.name} className="sb-league">
                <header className="sb-league-head">
                  <span className="sb-quick-emoji">{sportIcon(group.sport)}</span>
                  {group.name}
                  <span className="sb-count">{group.events.length} maç</span>
                </header>

                {bookMode ? (
                  <>
                    {/* Market seçici + sütun başlıkları: referansta liste
                        üstünde tek bir geniş kutu ve altında seçim adları. */}
                    <div className="sb-market-bar">
                      <div className="sb-market-select">
                        {group.events[0]?.markets?.[0]?.label || 'Maç Sonucu'}
                        <ChevronDown size={16} />
                      </div>
                      <div className="sb-market-cols">
                        {(group.events[0]?.markets?.[0]?.selections || []).map((selection) => (
                          <span key={selection.pick_key}>{selection.label}</span>
                        ))}
                      </div>
                    </div>
                    {group.events.map((event) => (
                      <EventRow
                        key={`${event.feed || 'sportsbook'}:${event.event_key}`}
                        event={event}
                        activePick={coupon.find((p) => p.match_id === event.event_key)}
                        onSelect={addBookPick}
                        onLoadMarkets={loadBookEventMarkets}
                      />
                    ))}
                  </>
                ) : (
                  group.events.map((match) => {
                    const activePick = coupon.find((p) => p.match_id === match.id);
                    return (
                      <div key={match.id} className="sb-event">
                        <div className="sb-event-time">
                          {match.live
                            ? (
                              <>
                                <strong>{match.elapsed ? `${match.elapsed}'` : ''}</strong>
                                <span className="sb-live">CANLI</span>
                              </>
                            )
                            : <span>{formatTime(match.date)}</span>}
                        </div>
                        <div className="sb-event-teams">
                          <div className="sb-team">
                            <TeamBadge name={match.home} logo={match.home_logo} size={18} />
                            <span>{match.home}</span>
                          </div>
                          <div className="sb-team">
                            <TeamBadge name={match.away} logo={match.away_logo} size={18} />
                            <span>{match.away}</span>
                          </div>
                        </div>
                        {/* Skor sütunu her zaman çizilir (boşken genişliği
                            sıfırdır): koşullu çizmek satırın sütun sayısını
                            değiştirip oranların hizasını bozuyordu. */}
                        <div className="sb-event-score">
                          {match.live && (
                            <>
                              <span>{match.score_home ?? 0}</span>
                              <span>{match.score_away ?? 0}</span>
                            </>
                          )}
                        </div>
                        <div className="sb-odds">
                          {match.betting_available && match.odds
                            ? ['1', 'X', '2'].map((key) => (
                              <OddButton
                                key={key}
                                selection={{
                                  pick_key: key,
                                  label: key,
                                  odd: match.odds[key],
                                  custom: (match.custom_odds || []).includes(key),
                                }}
                                selected={activePick?.pick_key === key}
                                onSelect={() => addPick(match, key)}
                              />
                            ))
                            : <span className="sb-odd-empty">—</span>}
                        </div>
                        {/* API-Football beslemesinde tek market (1X2) vardır;
                            ikinci market ve rozet sütunları boş durur ki
                            oranlar konsensüs listesiyle aynı hizada kalsın. */}
                        <div className="sb-odds sb-odds-alt" />
                        <span className="sb-markets-btn is-empty" />
                      </div>
                    );
                  })
                )}
              </section>
            ))}

            {!busy && !expandedAll && hiddenCount > 0 && (
              <div className="sb-showall-wrap">
                <button
                  type="button"
                  className="sb-showall"
                  onClick={() => setExpandedAll(true)}
                >
                  Tümünü Göster
                </button>
              </div>
            )}
          </div>
        </main>

        {/* Kupon varsayılan olarak kapalıdır ve listeye yer bırakır; ilk seçim
            yapılınca kendiliğinden açılır (aşağıdaki etki), kupon boşalınca
            tekrar kapanır. Oyuncu isterse başlıktan elle kapatabilir. */}
        {slipOpen && <BetSlip {...sb} onClose={() => setSlipOpen(false)} />}
      </div>

      {/* Kapalıyken sağda duran açma düğmesi: seçim sayısı ve toplam oran
          üstünde durur, kupona dönüş yolu her zaman görünür kalır. */}
      {!slipOpen && (
        <button
          type="button"
          className="sb-slip-fab"
          onClick={() => setSlipOpen(true)}
        >
          <Ticket size={18} />
          <span className="sb-slip-fab-count">{coupon.length}</span>
          {coupon.length > 0 && (
            <span className="sb-slip-fab-odd">{totalOdd.toFixed(2)}</span>
          )}
        </button>
      )}
    </div>
  );
};

export default SportsPage;
